import { useMemo, useState } from "react";
import { termCategories, terms } from "@/mocks/terms";
import type { Term } from "../dataTypes";

interface LexiconProps {
  keyword: string;
  setKeyword: (kw: string) => void;
  onPickTerm: (term: Term) => void;
}

function heatTone(hot: number) {
  if (hot >= 90) return "bg-accent-100 text-accent-800";
  if (hot >= 80) return "bg-primary-100 text-primary-800";
  return "bg-secondary-100 text-secondary-900";
}

export default function Lexicon({ keyword, setKeyword, onPickTerm }: LexiconProps) {
  const [category, setCategory] = useState("全部");

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return (terms as Term[]).filter((t) => {
      const matchCat = category === "全部" || t.category === category;
      const matchKw =
        !kw ||
        t.title.toLowerCase().includes(kw) ||
        t.summary.toLowerCase().includes(kw) ||
        t.tag.toLowerCase().includes(kw);
      return matchCat && matchKw;
    });
  }, [category, keyword]);

  return (
    <section id="lexicon" className="scroll-mt-16 bg-background-100">
      <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
        {/* 标题 */}
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold tracking-[0.2em] text-accent-600">
              <i className="ri-book-2-line text-sm leading-none"></i>
              名词速览 LEXICON
            </span>
            <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              热搜背后的黑话，一看就懂
            </h2>
            <p className="mt-3 max-w-2xl text-sm leading-6 text-foreground-600">
              从网络流行梗到行业黑话，从典故来源到法律常识。点击任意词条，即可查看完整的释义、溯源与用法。
            </p>
          </div>
          <label className="flex w-full max-w-xs items-center gap-2 rounded-lg bg-background-50 px-3.5 ring-1 ring-background-300 focus-within:ring-2 focus-within:ring-primary-400 md:mb-1">
            <i className="ri-search-line text-sm leading-none text-foreground-400"></i>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              type="text"
              placeholder="搜索词条…"
              className="w-full bg-transparent py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
            />
            {keyword && (
              <button
                aria-label="清空搜索"
                onClick={() => setKeyword("")}
                className="text-foreground-400 hover:text-foreground-700 cursor-pointer"
              >
                <i className="ri-close-circle-fill text-base leading-none"></i>
              </button>
            )}
          </label>
        </div>

        {/* 分类筛选 */}
        <div className="mt-7 flex flex-wrap gap-2">
          {termCategories.map((c) => {
            const active = category === c.key;
            return (
              <button
                key={c.key}
                onClick={() => setCategory(c.key)}
                className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors cursor-pointer ${
                  active
                    ? "bg-primary-600 text-background-50"
                    : "bg-background-50 text-foreground-600 ring-1 ring-background-300 hover:bg-primary-100/60 hover:text-primary-800"
                }`}
              >
                {c.label}
              </button>
            );
          })}
          <span className="ml-auto hidden items-center text-xs text-foreground-400 sm:inline-flex">
            共 {filtered.length} 个词条
          </span>
        </div>

        {/* 词条网格 */}
        {filtered.length > 0 ? (
          <div className="mt-7 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filtered.map((term) => (
              <article
                key={term.id}
                className="group flex flex-col rounded-lg bg-background-50 p-5 ring-1 ring-background-200 transition-all hover:-translate-y-0.5 hover:ring-primary-300 cursor-pointer"
                onClick={() => onPickTerm(term)}
              >
                <div className="flex items-center justify-between">
                  <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-medium text-secondary-900">
                    {term.tag}
                  </span>
                  <span
                    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${heatTone(
                      term.hot
                    )}`}
                  >
                    <i className="ri-fire-line text-xs leading-none"></i>
                    {term.hot}
                  </span>
                </div>

                <h3 className="mt-3 font-heading text-xl font-bold text-foreground-950 group-hover:text-primary-700">
                  {term.title}
                </h3>
                <p className="mt-2 line-clamp-3 flex-1 text-sm leading-6 text-foreground-600">
                  {term.summary}
                </p>

                <div className="mt-4 flex items-center justify-between border-t border-background-100 pt-3">
                  <span className="text-xs text-foreground-400">{term.category}</span>
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 transition-colors group-hover:text-primary-700">
                    查看详情
                    <i className="ri-arrow-right-line text-sm leading-none transition-transform group-hover:translate-x-0.5"></i>
                  </span>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="mt-7 flex flex-col items-center justify-center rounded-lg bg-background-50 px-6 py-16 text-center ring-1 ring-background-200">
            <i className="ri-emotion-sad-line text-4xl leading-none text-primary-300"></i>
            <p className="mt-3 text-sm text-foreground-600">
              没有找到与「{keyword}」相关的词条，换个关键词试试？
            </p>
            <button
              onClick={() => {
                setKeyword("");
                setCategory("全部");
              }}
              className="mt-4 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2 text-sm font-medium text-background-50 hover:bg-primary-700 cursor-pointer"
            >
              查看全部词条
            </button>
          </div>
        )}
      </div>
    </section>
  );
}